/** **어느 자리에 어느 판을 그리는가.** 화면이 이 판단을 각자 하지 않게 한 곳에 둔다.
 *
 * ⚠️ **앱(`apps/mobile/src/lib/rendition.ts`)에 같은 정책이 있다. 한쪽만 고치면 두 화면이
 * 같은 장을 다르게 그린다.** 판단 자체는 `docs/PRD.md` 7.5.14 가 정본이고 이 둘은 구현이다.
 * (제자리는 `packages/shared` 이지만 그 폴더는 `data-manager` 소관이라 옮기지 않았다.)
 *
 * ## 왜 있는가 (2026-09-19)
 *
 * 화면들이 전부 `absoluteUrl(image.outputUrl)` 을 그리고 있었다. 그 주소가 가리키는 것은
 * **누끼 원본**이고 실측 9.03 MB · 3024 × 3350 이라 디코드에 40.5 MB 를 먹는다. 진행 화면은
 * 그것을 34pt 썸네일로 여덟 개 걸었다. **324 MB 다.** 안드로이드는 그때 말없이 그 장을 버려
 * 빈 칸만 남기고, 셀러는 그것을 "누끼가 안 됐다"로 읽는다. **누끼는 됐다.**
 *
 * 규격의 정본은 `docs/PRD.md` 7.5.14 다. 그 절이 판을 둘로 정했다.
 *   - `thumb` (320px) — 줄·칸에 붙는 작은 그림. **여러 장을 늘어놓는 자리**
 *   - `preview` (1280px) — 한 장을 크게 보는 자리. 셀러가 **누끼 가장자리를 판정**하는 화면
 *
 * ⛔ **`outputUrl` 을 화면에 직접 쓰지 마라.** 쓸 자리는 내보내기뿐이고, 그것은 서버가
 * 한다. 새 화면을 만들 때도 이 파일의 둘 중 하나를 쓴다.
 */

import type { JobImageView } from "@makit/shared";

/** 여러 장을 늘어놓는 자리(`thumb`).
 *
 * ## 떨어지는 순서에 뜻이 있다
 *
 * 1. `thumbUrl` — 이 자리를 위해 만든 판이다.
 * 2. `previewUrl` — **`thumb` 만 실패했을 때.** 1280px 은 34pt 에 과하지만 5.9 MB 이고,
 *    원본은 40.5 MB 다. 판 둘은 R2 put 도 둘이라 **한쪽만 실패할 수 있다**(그 경우가
 *    `JobImageView` 주석에 적혀 있다). 그때 굳이 제일 나쁜 것으로 갈 이유가 없다.
 * 3. `outputUrl` — 둘 다 없을 때. **이것이 지금 앱을 죽이는 그림이다.**
 *
 * ⚠️ **3번으로 떨어지는 것이 옳은지에는 이견이 있다.** `docs/PRD.md` 7.5.14 는 "판이 없는
 * 장은 앱이 원본으로 떨어진다"로 정했고, `docs/DATA_DICTIONARY.md` 24장은 **떨어지지 말고
 * 백필하라**고 적었다. 지금 코드가 7.5.14 를 따르는 이유는 **아직 아무 장에도 판이 없기
 * 때문**이다 — 떨어지지 않으면 이미 만들어 둔 작업은 그림이 통째로 사라진다. 백필이 돌고
 * 나면 이 3번 줄을 지우는 것이 맞고, **그때 지울 수 있게 한 줄로 모아 뒀다.** */
export function thumbUrlOf(image: JobImageView): string | null {
  return image.thumbUrl ?? image.previewUrl ?? image.outputUrl;
}

/** 한 장을 크게 보는 자리(`preview`).
 *
 * ⛔ **`thumbUrl` 로 떨어지지 않는다.** 320px 을 404pt 박스에 늘리면 **가장자리가 뭉개지고**,
 * 이 화면(`A-51`)은 셀러가 바로 그 가장자리를 보고 `A-25`(장당 ₩500)를 누를지 정하는
 * 자리다(`docs/PRD.md` 7.5.14). **돈이 걸린 판단을 흐린 그림 위에 두지 않는다** — 차라리
 * 무거운 원본을 그린다. 여기는 한 번에 한 장이라 목록과 위험의 크기도 다르다. */
export function previewUrlOf(image: JobImageView): string | null {
  return image.previewUrl ?? image.outputUrl;
}
